// ---------------------------------------------------------------------------
// useTeamSessionSocket.ts — connects to the realtime gateway for a team
// session and pipes incoming events into the Redux store.
// ---------------------------------------------------------------------------
import { useEffect, useRef } from "react";
import { useAppDispatch } from "../store/hooks";
import { getSocket } from "../services/socketService";
import type { Task, Note, TeamCheckin } from "../store/slices/teamFocusSession/types";

// Synchronous Redux actions used to apply realtime patches to the store.
import {
    addTask,
    editTask,
    deleteTask,
    addNote,
    editNote,
    deleteNote,
    addCheckin,
    updateCheckin,
    deleteCheckin,
    updateTeamSessionState,
} from "../store/slices/teamFocusSessionSlice";

/**
 * Hook that manages the socket lifecycle for a team focus session.
 *
 * - Connects the socket (if not already connected)
 * - Joins the room for the given `teamId`
 * - Subscribes to all entity events and dispatches Redux actions
 * - Leaves the room + unsubscribes on cleanup / when `teamId` changes
 */
export function useTeamSessionSocket(teamId: string | undefined) {

    const dispatch = useAppDispatch();
    // Keep track of the room we joined so we can cleanly leave on unmount.
    const joinedRoomRef = useRef<string | null>(null);

    useEffect(() => {
        if (!teamId) return;

        // cancelled flag guards against React Strict Mode's double-invoke:
        // the first (discarded) mount's cleanup sets this to true so its
        // joinRoom callback is a no-op even if it fires late.
        let cancelled = false;

        const socket = getSocket();

        // ---- Join the session room once connected ---------------------------
        const joinRoom = () => {
            if (cancelled) return; // stale invocation from Strict Mode — ignore
            if (import.meta.env.DEV) {
                console.log("[socket] joinRoom fired, socket.connected:", socket.connected);
            }
            socket.emit("join-team-session", { sessionId: teamId }, (ack: any) => {
                if (import.meta.env.DEV) {
                    console.log("[socket] joined room ack:", ack);
                }
            });
            joinedRoomRef.current = teamId;
        };

        if (import.meta.env.DEV) {
            console.log("[socket] useEffect running — teamId:", teamId, "| connected:", socket.connected, "| active:", socket.active);
        }

        if (socket.connected) {
            joinRoom();
        } else {
            socket.once("connect", joinRoom);
            // socket.active is true while a connect() is already in-flight;
            // skip the call to avoid duplicate handshakes on Strict Mode's
            // second mount.
            if (!socket.active) {
                socket.connect();
            }
        }


        // ---- Event handlers --------------------------------------------------

        // --- Tasks ---
        const onTaskCreated = (task: Task) => {
            dispatch(addTask({ teamId, task }));
        };
        const onTaskUpdated = (task: Task) => {
            dispatch(
                editTask({ teamId, taskId: task.id, updates: task }),
            );
        };
        const onTaskDeleted = ({ taskId }: { taskId: string }) => {
            dispatch(deleteTask({ teamId, taskId }));
        };

        // --- Notes ---
        const onNoteCreated = (note: Note) => {
            dispatch(addNote({ teamId, note }));
        };
        const onNoteUpdated = (note: Note) => {
            dispatch(editNote({ teamId, noteId: note.id, text: note.note }));
        };
        const onNoteDeleted = ({ noteId }: { noteId: string }) => {
            console.log("[socket] Note deleted event received for noteId:", noteId);
            dispatch(deleteNote({ teamId, noteId }));
        };

        // --- Check-ins ---
        const onCheckinCreated = (checkin: TeamCheckin) => {
            dispatch(addCheckin({ teamId, checkin }));
        };
        const onCheckinUpdated = (checkin: TeamCheckin) => {
            dispatch(
                updateCheckin({ teamId, checkinId: checkin.id, updates: checkin }),
            );
        };
        const onCheckinDeleted = ({ checkinId }: { checkinId: string }) => {
            dispatch(deleteCheckin({ teamId, checkinId }));
        };

        // --- Timer ---
        const onSessionTimer = (payload: {
            action: string;
            time?: string;
            userId: string;
        }) => {
            if (import.meta.env.DEV) {
                console.log("[socket] session.timer event:", payload);
            }
            const updates: any = {};

            if (payload.action === 'start') {
                if (payload.time) updates.sessionStartedAt = payload.time;
            } else if (payload.action === 'pause') {
                updates.sessionStartedAt = null;
            } else if (payload.action === 'end') {
                updates.sessionStartedAt = null;
            }
            dispatch(updateTeamSessionState({ teamId, updates }));
        };

        // --- Session Ended ---
        const onSessionEnded = (payload: { endedByUserId: string }) => {
            if (import.meta.env.DEV) {
                console.log("[socket] session.ended event:", payload);
            }
            dispatch(updateTeamSessionState({
                teamId,
                updates: {
                    sessionStartedAt: null,
                    sessionEndedByHost: true,
                }
            }));
        };

        // --- Participant ---
        const onSessionParticipant = (payload: { action: string; user: any }) => {
            if (import.meta.env.DEV) {
                console.log("[socket] session.participant event:", payload);
            }
            // Participants are primarily handled by Livekit directly, so we just log.
        };

        // ---- Subscribe -------------------------------------------------------
        socket.on("tasks.created", onTaskCreated);
        socket.on("tasks.updated", onTaskUpdated);
        socket.on("tasks.deleted", onTaskDeleted);

        socket.on("notes.created", onNoteCreated);
        socket.on("notes.updated", onNoteUpdated);
        socket.on("notes.deleted", onNoteDeleted);

        socket.on("checkins.created", onCheckinCreated);
        socket.on("checkins.updated", onCheckinUpdated);
        socket.on("checkins.deleted", onCheckinDeleted);

        socket.on("session.timer", onSessionTimer);
        socket.on("session.participant", onSessionParticipant);
        socket.on("session.ended", onSessionEnded);

        // ---- Cleanup ---------------------------------------------------------
        return () => {
            cancelled = true;

            // Remove the pending once-listener so joinRoom doesn't fire
            socket.off("connect", joinRoom);

            // Leave the room
            if (joinedRoomRef.current) {
                socket.emit("leave-team-session", {
                    sessionId: joinedRoomRef.current,
                });
                joinedRoomRef.current = null;
            }

            // Unsubscribe all listeners we registered
            socket.off("tasks.created", onTaskCreated);
            socket.off("tasks.updated", onTaskUpdated);
            socket.off("tasks.deleted", onTaskDeleted);

            socket.off("notes.created", onNoteCreated);
            socket.off("notes.updated", onNoteUpdated);
            socket.off("notes.deleted", onNoteDeleted);

            socket.off("checkins.created", onCheckinCreated);
            socket.off("checkins.updated", onCheckinUpdated);
            socket.off("checkins.deleted", onCheckinDeleted);

            socket.off("session.timer", onSessionTimer);
            socket.off("session.participant", onSessionParticipant);
            socket.off("session.ended", onSessionEnded);
        };
    }, [teamId, dispatch]);
}
